import React, { lazy, Suspense } from 'react';
import HomePage from './HomePage/HomePage.js';
import { APP_ICONS } from '../static/appIcons.js';
import FilterContextProvider from './reusable/Filters/filterContext.js';
import DataVisualizationContextProvider from './DataVisualizations/DataVisualizationContext.js';
import CostAllocationContextProvider from './CostAllocation/CostAllocationContext.js';
import BudgetRequestsContextProvider from './BudgetRequests/BudgetRequestsContext.js';
import ProgramInventoryContextProvider from './ProgramInventory/programInventoryContext.js';
import BudgetUploadContextProvider from './BudgetSummary/BudgetUpload/budgetUploadContext.js';
import PBBStrategist from './PBBStrategist/PBBStrategist.js';
import { NAVIGATION_PATHS } from '../static/navigationPaths.js';
import ProgramInsightsContextProvider from './ProgramInsights/ProgramInsightsContext.js';
import GeneralErrorPage from './reusable/ErrorPages/GeneralErrorPage.js';
import { getErrorPagesByType } from './reusable/ErrorPages/errorPagesHelpers.js';
import CostBenchmark from './CostBenchmark/CostBenchmark.js';
import { CostBenchmarkContextProvider } from '../contexts/costBenchmarkingContext.js';
import { Outlet } from 'react-router';
import { SelectionProvider } from './SelectionContext.js';
import { ChatActionsProvider } from './PBBStrategist/ChatActionsContext.js';
import AiAnalytics from './AiAnalytics/AiAnalytics.js';
import PeerReviewContextProvider from './PeerReview/peerReviewContext.js';
import FinalReviewContextProvider from '../contexts/finalReviewContext.js';
import ScoreCellRenderContextProvider from '../contexts/scoreCellRenderContext.js';
import { DepartmentScoringProvider } from '../contexts/departmentScoringContext.js';
import AiInsightsContextProvider from '../contexts/aiInsightsContext.js';

const NameConvention = lazy(() => import('./NameConvention/NameConvention.js'));
const Reports = lazy(() => import('./Reports/Reports.js'));
const ManageUsers = lazy(() => import('./ManageUsers/ManageUsers.js'));
const ManageUserAccess = lazy(
  () => import('./Access/ManageUserAccess/ManageUserAccess.js'),
);
const ManageOrganizationAccess = lazy(
  () => import('./Access/ManageOrganizationAccess/ManageOrganizationAccess.js'),
);
const OrganizationEditor = lazy(
  () => import('./OrganizationEditor/OrganizationEditor.js'),
);
const OrganizationalHierarchy = lazy(
  () => import('./OrganizationHierarchy/OrganizationHierarchy.js'),
);
const ProgramInventory = lazy(
  () => import('./ProgramInventory/ProgramInventory.js'),
);
const BudgetSummary = lazy(() => import('./BudgetSummary/BudgetSummary.js'));
const ConfigureBudget = lazy(
  () => import('./ConfigureBudget/ConfigureBudget.js'),
);
const UserActivity = lazy(() => import('./UserActivity/UserActivity.js'));
const UserProfile = lazy(() => import('./UserProfile/UserProfile.js'));
const ConfigureRequests = lazy(
  () => import('./ConfigureRequests/ConfigureRequests.js'),
);
const ConfigureResults = lazy(
  () => import('./ConfigureResults/ConfigureResults.js'),
);
const ManageAdminUsers = lazy(
  () => import('./ManageAdminUser/ManageAdminUsers.js'),
);
const ManageData = lazy(() => import('./ManageData/ManageData.js'));
const ConfigureInsights = lazy(
  () => import('./ConfigureInsights/ConfigureInsights.js'),
);
const BudgetLineItemEditor = lazy(
  () => import('./BudgetLineItemEditor/BudgetLineItemEditor.js'),
);
const DataVisualizations = lazy(
  () => import('./DataVisualizations/DataVisualizations.js'),
);
const ProgramInsightsMain = lazy(
  () => import('./ProgramInsights/ProgramInsightsMain.js'),
);
const CostAllocation = lazy(() => import('./CostAllocation/CostAllocation.js'));
const DepartmentScoring = lazy(
  () => import('./DepartmentScoring/DepartmentScoring.js'),
);
const PeerReview = lazy(() => import('./PeerReview/PeerReview.js'));

const BudgetRequests = lazy(() => import('./BudgetRequests/BudgetRequests.js'));
const DepartmentScoringReview = lazy(
  () => import('./DepartmentScoringReview/DepartmentScoringReview.js'),
);
const FinalReview = lazy(() => import('./FinalReview/FinalReview.js'));

const BudgetContextLayout = () => {
  return (
    <SelectionProvider>
      <Outlet /> {/* This renders your pages (Home, Reports, etc.) */}
    </SelectionProvider>
  );
};

const getAppRoutes = (nameConventions) => {
  return [
    {
      path: NAVIGATION_PATHS.BASE_PARAMS,
      element: <BudgetContextLayout />,
      protected: true,
      children: [
        /* { index: true, element: <Navigate to={NAVIGATION_PATHS.HOME} replace /> }, */
        {
          index: true,
          path: NAVIGATION_PATHS.HOME,
          element: <HomePage />,
          protected: false
        },
        {
          path: NAVIGATION_PATHS.NAME_CONVENTION,
          element: <NameConvention />,
          protected: true,
          accessIcon: APP_ICONS.NAME_CONVENTIONS,
        },
        {
          path: NAVIGATION_PATHS.USER_PROFILE,
          element: <UserProfile />,
          protected: false,
        },
        {
          path: NAVIGATION_PATHS.REPORTS,
          element: <Reports />,
          protected: true,
          accessIcon: APP_ICONS.REPORTS,
        },
        {
          path: NAVIGATION_PATHS.MANAGE_USERS,
          element: <ManageUsers />,
          protected: true,
          accessIcon: APP_ICONS.MANAGE_USERS,
        },
        {
          path: NAVIGATION_PATHS.MANAGE_USER_ACCESS,
          element: <ManageUserAccess />,
          protected: true,
          accessIcon: APP_ICONS.MANAGE_USER_ACCESS,
        },
        {
          path: NAVIGATION_PATHS.MANAGE_ORGANIZATION_ACCESS,
          element: <ManageOrganizationAccess />,
          protected: true,
          accessIcon: APP_ICONS.MANAGE_ORGANIZATION_ACCESS,
        },
        {
          path: NAVIGATION_PATHS.ORGANIZATION_EDITOR,
          element: <OrganizationEditor />,
          protected: true,
          accessIcon: APP_ICONS.ORGANIZATION_EDITOR,
        },
        {
          path: NAVIGATION_PATHS.ORGANIZATIONAL_HIERARCHY,
          element: <OrganizationalHierarchy />,
          protected: true,
          accessIcon: APP_ICONS.ORGANIZATIONAL_HIERARCHY,
        },
        {
          path: NAVIGATION_PATHS.PROGRAM_INVENTORY,
          element: (
            <ProgramInventoryContextProvider>
              <AiInsightsContextProvider>
                <ProgramInventory />
              </AiInsightsContextProvider>
            </ProgramInventoryContextProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.PROGRAM_INVENTORY,
        },
        {
          path: NAVIGATION_PATHS.DATA_VISUALIZATIONS,
          element: (
            <FilterContextProvider>
              <DataVisualizationContextProvider>
                <DataVisualizations />
              </DataVisualizationContextProvider>
            </FilterContextProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.DATA_VISUALIZATION,
        },
        {
          path: NAVIGATION_PATHS.BUDGET_SUMMARY,
          element: (
            <BudgetUploadContextProvider>
              <BudgetSummary />
            </BudgetUploadContextProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.BUDGET_SUMMARY,
        },
        {
          path: NAVIGATION_PATHS.CONFIGURE_BUDGET,
          element: <ConfigureBudget />,
          protected: true,
          accessIcon: APP_ICONS.CONFIGURE_BUDGET,
        },
        {
          path: NAVIGATION_PATHS.DEPARTMENT_SCORING,
          element: (
            <FilterContextProvider>
              <DepartmentScoringProvider>
                <DepartmentScoring />
              </DepartmentScoringProvider>
            </FilterContextProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.DEPARTMENT_SCORING,
        },
        {
          path: NAVIGATION_PATHS.DEPARTMENT_REVIEW,
          element: (
            <FilterContextProvider>
              <DepartmentScoringReview />
            </FilterContextProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.DEPARTMENT_REVIEW,
        },
        {
          path: NAVIGATION_PATHS.PEER_REVIEW,
          element: (
            <FilterContextProvider>
              <PeerReviewContextProvider>
                <PeerReview />
              </PeerReviewContextProvider>
            </FilterContextProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.PEER_REVIEW,
        },
        {
          path: NAVIGATION_PATHS.FINAL_REVIEW,
          element: (
            <FilterContextProvider>
              <FinalReviewContextProvider>
                <ScoreCellRenderContextProvider>
                  <FinalReview />
                </ScoreCellRenderContextProvider>
              </FinalReviewContextProvider>
            </FilterContextProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.FINAL_REVIEW,
        },
        {
          path: NAVIGATION_PATHS.USER_ACTIVITY,
          element: <UserActivity />,
          protected: true,
          accessIcon: APP_ICONS.USER_ACTIVITY,
        },
        {
          path: NAVIGATION_PATHS.COST_ALLOCATION,
          element: (
            <FilterContextProvider>
              <CostAllocationContextProvider>
                <CostAllocation />
              </CostAllocationContextProvider>
            </FilterContextProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.COST_ALLOCATION,
        },
        {
          path: NAVIGATION_PATHS.CONFIGURE_REQUESTS,
          element: <ConfigureRequests />,
          protected: true,
          accessIcon: APP_ICONS.CONFIGURE_REQUESTS,
        },
        {
          path: NAVIGATION_PATHS.CONFIGURE_SCORING,
          element: <ConfigureResults />,
          protected: true,
          accessIcon: APP_ICONS.CONFIGURE_SCORING,
        },
        {
          path: NAVIGATION_PATHS.MANAGE_ADMIN_USERS,
          element: <ManageAdminUsers />,
          protected: true,
          accessIcon: APP_ICONS.MANAGE_ADMIN_USERS,
        },
        {
          path: NAVIGATION_PATHS.PROGRAM_INSIGHTS,
          element: (
            <FilterContextProvider>
              <ProgramInsightsContextProvider>
                <AiInsightsContextProvider>
                  <ProgramInsightsMain />
                </AiInsightsContextProvider>
              </ProgramInsightsContextProvider>
            </FilterContextProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.PROGRAM_INSIGHTS,
        },
        {
          path: NAVIGATION_PATHS.BUDGET_LINE_ITEM_EDITOR,
          element: 
            <BudgetUploadContextProvider>
              <BudgetLineItemEditor />
            </BudgetUploadContextProvider>,
          protected: true,
          accessIcon: APP_ICONS.BUDGET_LINE_ITEM_EDITOR,
        },
        {
          path: NAVIGATION_PATHS.BUDGET_REQUESTS,
          element: (
            <FilterContextProvider>
              <BudgetRequestsContextProvider>
                <BudgetRequests />
              </BudgetRequestsContextProvider>
            </FilterContextProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.BUDGET_REQUESTS,
        },
        {
          path: NAVIGATION_PATHS.CONFIGURE_INSIGHTS,
          element: <ConfigureInsights />,
          protected: true,
          accessIcon: APP_ICONS.CONFIGURE_INSIGHTS,
        },
        {
          path: NAVIGATION_PATHS.MANAGE_DATA,
          element: <ManageData />,
          protected: true,
          accessIcon: APP_ICONS.MANAGE_DATA,
        },
        {
          path: NAVIGATION_PATHS.PBB_STRATEGIST,
          element: (
            <ChatActionsProvider>
              <PBBStrategist />
            </ChatActionsProvider>
          ),
          protected: true,
          accessIcon: APP_ICONS.PBB_STRATEGIST,
        },
        {
          path: NAVIGATION_PATHS.COST_BENCHMARK,
          element: (
            <FilterContextProvider>
              <CostBenchmarkContextProvider>
                <Suspense>
                  <CostBenchmark />
                </Suspense>
              </CostBenchmarkContextProvider>
            </FilterContextProvider>),
          protected: true,
          accessIcon: APP_ICONS.COST_BENCHMARK,
        },
        {
          path: NAVIGATION_PATHS.AI_ANALYTICS,
          element: (
            <FilterContextProvider>
              <Suspense>
                <AiAnalytics />
              </Suspense>
            </FilterContextProvider>),
          protected: true,
          accessIcon: APP_ICONS.AI_ANALYTICS,
        },
        {
          path: '*',
          element: <GeneralErrorPage type={getErrorPagesByType(nameConventions).PAGE_NOT_FOUND} />,
          protected: true,
        },
        // EXPERIMENTAL ROUTES
        // JUST FOR TESTING
        {
          path: 'comingSoon',
          element: <GeneralErrorPage type={getErrorPagesByType(nameConventions).COMING_SOON} />,
          protected: true,
        },
        {
          path: 'outOfDateBrowser',
          element: (
            <GeneralErrorPage type={getErrorPagesByType(nameConventions).PAGE_OUT_OF_DATE_BROWSER} />
          ),
          protected: true,
        },
        {
          path: 'unsupportedBrowser',
          element: (
            <GeneralErrorPage type={getErrorPagesByType(nameConventions).PAGE_UNSUPPORTED_BROWSER} />
          ),
          protected: true,
        },
      ],
    }
    //{ path: '/', element: <Navigate to="/my-org/my-budget/home" replace /> },
  ];
};


export default getAppRoutes;
